/**
 * DELTE PRODUKSJONER (bestillingen §5.1, Atle 27.10.2026) — `GET /v1/me/shares/productions`.
 *
 * «Lista har én rad per festival eller arrangør, med hva som er delt, nivå og utløp. Et klikk åpner
 * gjestemodus.» Radene er personens egne levende delinger fra organisasjoner hen ikke er medlem av,
 * samlet per organisasjon og festival. Artistene står i «Delte artister» og er ikke med her.
 */
import { z } from 'zod';

import { shareObjectTypeSchema, sharePermissionSchema } from './shares.js';
import { wireInstant } from './wire-time.js';

/** Ett delt objekt i en rad. */
export const mySharedProductionItemSchema = z
  .object({
    grantId: z.uuid(),
    objectType: shareObjectTypeSchema,
    title: z.string(),
    /** Årgangen objektet står i, når det har en. */
    editionName: z.string().nullable(),
    permission: sharePermissionSchema,
    expiresAt: wireInstant.nullable(),
  })
  .meta({ id: 'MySharedProductionItem' });
export type MySharedProductionItem = z.infer<typeof mySharedProductionItemSchema>;

export const mySharedProductionSchema = z
  .object({
    /** `<organisasjon>:<festival>` — stabil nøkkel for lista. */
    key: z.string(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    /** Null for det som ikke hører til en festival (arrangøren selv, salen). */
    festivalId: z.uuid().nullable(),
    festivalName: z.string().nullable(),
    items: z.array(mySharedProductionItemSchema).min(1),
    /** Det høyeste nivået i raden. */
    permission: sharePermissionSchema,
    /** Når den siste delingen i raden utløper; null når en av dem ikke har utløp. */
    expiresAt: wireInstant.nullable(),
    /**
     * Delingen et klikk åpner (`/delt/<grantId>`): den bredeste i raden — årgangen, så avdelingen, så
     * scenen, så resten i tidsrekkefølge.
     */
    openGrantId: z.uuid(),
  })
  .meta({ id: 'MySharedProduction' });
export type MySharedProduction = z.infer<typeof mySharedProductionSchema>;

export const mySharedProductionListSchema = z
  .object({ items: z.array(mySharedProductionSchema) })
  .meta({ id: 'MySharedProductionList' });
export type MySharedProductionList = z.infer<typeof mySharedProductionListSchema>;

/** Hvilken deling i en rad et klikk åpner: den bredeste først (§5.1). */
export const SHARED_PRODUCTION_OPEN_ORDER: readonly string[] = Object.freeze([
  'edition',
  'department',
  'stage',
  'performance',
  'shift',
  'resource',
]);
